import { nowIso } from "@/lib/uid";

/**
 * Tiny in-memory ring buffer of recent console/errors, used to pre-fill a bug
 * report. Session-only: nothing is persisted and nothing is sent unless the user
 * explicitly submits a reviewed bug report.
 */

export interface CapturedError {
  at: string;
  message: string;
}

const MAX_ENTRIES = 20;
const buffer: CapturedError[] = [];
let installed = false;

function record(message: string): void {
  buffer.push({ at: nowIso(), message: message.slice(0, 1000) });
  if (buffer.length > MAX_ENTRIES) buffer.shift();
}

/** Patch console.error and global error handlers to capture recent errors. */
export function installConsoleCapture(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const original = console.error.bind(console);
  console.error = (...args: unknown[]): void => {
    record(args.map((a) => (a instanceof Error ? a.message : String(a))).join(" "));
    original(...args);
  };

  window.addEventListener("error", (event) => record(event.message));
  window.addEventListener("unhandledrejection", (event) => record(String(event.reason)));
}

/** Snapshot of recent captured errors (newest last). */
export function getRecentErrors(): CapturedError[] {
  return [...buffer];
}

/** Clear the buffer (used by tests). */
export function clearCapturedErrors(): void {
  buffer.length = 0;
}
